import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { ApiError } from '../services/apiClient';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void }) => string;
      reset?: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

function flattenErrors(data: Record<string, unknown>): string {
  if (typeof data.detail === 'string') return data.detail;
  const parts: string[] = [];
  for (const [, v] of Object.entries(data)) {
    if (v === data.detail) continue;
    if (typeof v === 'string') parts.push(v);
    else if (Array.isArray(v) && v.length && typeof v[0] === 'string') parts.push(v[0]);
    else if (v && typeof v === 'object') parts.push(flattenErrors(v as Record<string, unknown>));
  }
  return parts.join(' ') || 'Registration failed';
}

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    password_confirm: '',
    tenant_name: '',
    phone: '',
    accept_terms: false,
    website: '',
  });
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef<HTMLDivElement>(null);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current) return;
    const el = turnstileRef.current;
    const tryRender = () => {
      if (window.turnstile && el) {
        window.turnstile.render(el, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (t: string) => setTurnstileToken(t),
        });
      }
    };
    if (document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) {
      tryRender();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.onload = () => tryRender();
    document.body.appendChild(script);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.password_confirm) {
      setError('Passwords do not match');
      return;
    }
    if (!formData.accept_terms) {
      setError('Please agree to the terms and privacy policy.');
      return;
    }

    setLoading(true);

    try {
      await register({
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        password: formData.password,
        password_confirm: formData.password_confirm,
        tenant_name: formData.tenant_name.trim(),
        phone: formData.phone.trim(),
        accept_terms: true,
        website: formData.website,
        turnstile_token: turnstileToken,
      });
      navigate('/onboarding/vertical');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(flattenErrors(err.data));
      } else {
        setError(err instanceof Error ? err.message : 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-app flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-primary to-primary-dark rounded-2xl flex items-center justify-center mb-4">
            <span className="text-2xl font-bold text-primary-fg">LP</span>
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary-dark bg-clip-text text-transparent">
            LaunchPoint
          </h1>
          <p className="mt-2 text-fg-secondary">Start your 14-day free trial today.</p>
        </div>

        <div className="bg-surface rounded-2xl shadow-xl border border-border p-8">
          <form className="space-y-6" onSubmit={handleSubmit} noValidate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-fg mb-2">
                  First Name
                </label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  required
                  autoComplete="given-name"
                  className="input-field rounded-xl py-3"
                  placeholder="First name"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-fg mb-2">
                  Last Name
                </label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  required
                  autoComplete="family-name"
                  className="input-field rounded-xl py-3"
                  placeholder="Last name"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-fg mb-2">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input-field rounded-xl py-3"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="tenant_name" className="block text-sm font-medium text-fg mb-2">
                Company Name
              </label>
              <input
                id="tenant_name"
                name="tenant_name"
                type="text"
                required
                autoComplete="organization"
                className="input-field rounded-xl py-3"
                placeholder="Your company name"
                value={formData.tenant_name}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-fg mb-2">
                Phone <span className="text-fg-muted font-normal">(optional)</span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                className="input-field rounded-xl py-3"
                placeholder="Phone number"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <div className="sr-only" aria-hidden="true">
              <label htmlFor="website">Leave this field blank</label>
              <input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                className="input-field rounded-xl py-2"
                value={formData.website}
                onChange={handleChange}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-fg mb-2">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="input-field rounded-xl py-3"
                  placeholder="Create password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <p className="mt-1 text-xs text-fg-muted">At least 10 characters. Mix letters, numbers, and symbols.</p>
              </div>
              <div>
                <label htmlFor="password_confirm" className="block text-sm font-medium text-fg mb-2">
                  Confirm Password
                </label>
                <input
                  id="password_confirm"
                  name="password_confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="input-field rounded-xl py-3"
                  placeholder="Confirm password"
                  value={formData.password_confirm}
                  onChange={handleChange}
                />
              </div>
            </div>

            {TURNSTILE_SITE_KEY && (
              <div className="flex justify-center">
                <div ref={turnstileRef} />
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                name="accept_terms"
                type="checkbox"
                checked={formData.accept_terms}
                onChange={handleChange}
                className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm text-fg-secondary">
                I agree to the{' '}
                <a href="https://mambasolutionsllc.com/launchpoint/privacy-policy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="https://mambasolutionsllc.com/launchpoint/privacy-policy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                .
              </span>
            </label>

            {error && (
              <div className="alert-danger rounded-xl p-4">
                <p className="text-sm text-danger-fg">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-primary-fg bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-fg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating account...
                </>
              ) : (
                'Start Free Trial'
              )}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-surface text-fg-muted">Already have an account?</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/login"
                className="w-full flex justify-center py-3 px-4 border border-border rounded-xl shadow-sm text-sm font-medium text-fg bg-app hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
              >
                Sign in to your account
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-fg-muted">
            Join thousands of businesses already using LaunchPoint
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
